import type { AuthUser } from '@/shared/types/auth';
import type { AuthTokenResult, PasswordSessionRequest, PasswordSessionResponse } from './types';
import { PasswordAuthError } from './types';

const PASSWORD_AUTH_SOURCE_KEY = 'vision-lab.auth-source';
const PASSWORD_REFRESH_TOKEN_KEY = 'vision-lab.password-refresh-token';
const MAX_REFRESH_TOKEN_LENGTH = 8 * 1024;
const TOKEN_REFRESH_SKEW_MS = 60_000;

type PasswordStorageEvent = 'available' | 'cleared';

function isStoredRefreshToken(value: string | null): value is string {
  return (
    value !== null &&
    value.length > 0 &&
    value.length <= MAX_REFRESH_TOKEN_LENGTH &&
    Array.from(value).every((character) => {
      const codePoint = character.codePointAt(0) ?? 0;
      return codePoint > 32 && codePoint !== 127 && character !== ';' && character !== ',';
    })
  );
}

function readStoredRefreshToken(): string | null {
  try {
    const value = window.localStorage.getItem(PASSWORD_REFRESH_TOKEN_KEY);
    if (isStoredRefreshToken(value)) return value;
    window.localStorage.removeItem(PASSWORD_REFRESH_TOKEN_KEY);
  } catch {
    // Browser privacy settings may make persistent storage unavailable.
  }
  return null;
}

function decodeJwtClaims(token: string): Record<string, unknown> {
  const payload = token.split('.')[1];
  if (!payload) throw new Error('Invalid ID token');
  const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
  const bytes = Uint8Array.from(atob(padded), (character) => character.charCodeAt(0));
  const decoded = JSON.parse(new TextDecoder().decode(bytes)) as unknown;
  if (!decoded || typeof decoded !== 'object' || Array.isArray(decoded)) {
    throw new Error('Invalid ID token claims');
  }
  return decoded as Record<string, unknown>;
}

function tokenResult(token: string): AuthTokenResult {
  const claims = decodeJwtClaims(token);
  const authTimeSeconds = claims.auth_time;
  const firebaseClaim = claims.firebase;
  const signInProvider =
    firebaseClaim && typeof firebaseClaim === 'object' && !Array.isArray(firebaseClaim)
      ? (firebaseClaim as Record<string, unknown>).sign_in_provider
      : null;
  return {
    token,
    claims,
    authTime:
      typeof authTimeSeconds === 'number'
        ? new Date(authTimeSeconds * 1_000).toISOString()
        : String(authTimeSeconds ?? ''),
    signInProvider: typeof signInProvider === 'string' ? signInProvider : null,
  };
}

async function errorCode(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: unknown };
    return typeof body.error === 'string' ? body.error : 'authentication_failed';
  } catch {
    return 'authentication_failed';
  }
}

async function requestSession(
  path: 'login' | 'refresh',
  body: PasswordSessionRequest,
): Promise<PasswordSessionResponse> {
  const response = await fetch(`/api/auth/password/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
    cache: 'no-store',
    credentials: 'same-origin',
  });
  if (!response.ok) throw new PasswordAuthError(await errorCode(response), response.status);
  return (await response.json()) as PasswordSessionResponse;
}

export class PasswordAuthProvider {
  private refreshToken = typeof window === 'undefined' ? null : readStoredRefreshToken();
  private expiresAtMs = 0;
  private currentSession: PasswordSessionResponse | null = null;
  private refreshInFlight: Promise<PasswordSessionResponse> | null = null;
  private sessionGeneration = 0;

  hasStoredSession(): boolean {
    if (!this.refreshToken) return false;
    try {
      return window.localStorage.getItem(PASSWORD_AUTH_SOURCE_KEY) === 'password';
    } catch {
      return false;
    }
  }

  subscribeToStorage(listener: (event: PasswordStorageEvent) => void): () => void {
    if (typeof window === 'undefined') return () => {};
    const onStorage = (event: StorageEvent) => {
      if (event.storageArea !== window.localStorage) return;
      if (event.key === PASSWORD_REFRESH_TOKEN_KEY) {
        this.invalidateSession();
        this.refreshToken = isStoredRefreshToken(event.newValue) ? event.newValue : null;
        listener(this.refreshToken ? 'available' : 'cleared');
        return;
      }
      if (event.key === PASSWORD_AUTH_SOURCE_KEY && event.newValue === 'password') {
        const refreshToken = readStoredRefreshToken();
        if (!refreshToken) return;
        if (this.refreshToken !== refreshToken) {
          this.invalidateSession();
          this.refreshToken = refreshToken;
        }
        listener('available');
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }

  async restore(): Promise<AuthUser | null> {
    try {
      return (await this.refresh()).user;
    } catch {
      // A transient restore failure must not erase the refresh credential. `refresh` clears it
      // only for an explicit rejected/forbidden session, preserving the prior retry semantics.
      return null;
    }
  }

  async getAccessToken(forceRefresh = false): Promise<string | null> {
    if (!this.currentSession) return null;
    if (!forceRefresh && Date.now() < this.expiresAtMs - TOKEN_REFRESH_SKEW_MS) {
      return this.currentSession.idToken;
    }
    return (await this.refresh()).idToken;
  }

  async getTokenResult(forceRefresh = false): Promise<AuthTokenResult | null> {
    const token = await this.getAccessToken(forceRefresh);
    return token ? tokenResult(token) : null;
  }

  async login(email: string, password: string): Promise<AuthUser> {
    this.forget();
    const generation = this.sessionGeneration;
    const session = await requestSession('login', { email, password });
    if (!this.accept(session, generation)) {
      throw new PasswordAuthError('authentication_cancelled', 409);
    }
    return session.user;
  }

  async verifyAction(oobCode: string): Promise<string> {
    const response = await fetch('/api/auth/password/password-action/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ oobCode }),
      cache: 'no-store',
      credentials: 'same-origin',
    });
    if (!response.ok) throw new PasswordAuthError(await errorCode(response), response.status);
    const body = (await response.json()) as { email?: unknown };
    if (typeof body.email !== 'string') throw new PasswordAuthError('auth_unavailable', 503);
    return body.email;
  }

  async completeAction(oobCode: string, password: string): Promise<AuthUser> {
    this.forget();
    const generation = this.sessionGeneration;
    const response = await fetch('/api/auth/password/password-action/complete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ oobCode, password }),
      cache: 'no-store',
      credentials: 'same-origin',
    });
    if (!response.ok) throw new PasswordAuthError(await errorCode(response), response.status);
    const session = (await response.json()) as PasswordSessionResponse;
    if (!this.accept(session, generation)) {
      throw new PasswordAuthError('authentication_cancelled', 409);
    }
    return session.user;
  }

  async signOut(): Promise<void> {
    const generation = this.sessionGeneration;
    try {
      await fetch('/api/auth/password/logout', {
        method: 'POST',
        cache: 'no-store',
        credentials: 'same-origin',
      });
    } finally {
      if (this.sessionGeneration === generation) this.forget();
    }
  }

  clearStoredSession(): void {
    this.forget();
  }

  private accept(session: PasswordSessionResponse, generation: number): boolean {
    if (this.sessionGeneration !== generation) return false;
    this.storeRefreshToken(session.refreshToken);
    this.currentSession = session;
    this.expiresAtMs = Date.now() + session.expiresIn * 1_000;
    return true;
  }

  private async refresh(): Promise<PasswordSessionResponse> {
    if (this.refreshInFlight) return this.refreshInFlight;
    const refreshToken = this.refreshToken ?? readStoredRefreshToken();
    if (!refreshToken) throw new PasswordAuthError('session_expired', 401);
    const generation = this.sessionGeneration;
    const request = requestSession('refresh', { refreshToken })
      .then((session) => {
        this.accept(session, generation);
        return session;
      })
      .catch((error: unknown) => {
        if (
          this.sessionGeneration === generation &&
          error instanceof PasswordAuthError &&
          (error.status === 401 || error.status === 403)
        ) {
          this.forget();
        }
        throw error;
      })
      .finally(() => {
        if (this.refreshInFlight === request) this.refreshInFlight = null;
      });
    this.refreshInFlight = request;
    return request;
  }

  private storeRefreshToken(refreshToken: string): void {
    if (!isStoredRefreshToken(refreshToken)) throw new PasswordAuthError('auth_unavailable', 503);
    try {
      window.localStorage.setItem(PASSWORD_REFRESH_TOKEN_KEY, refreshToken);
      window.localStorage.setItem(PASSWORD_AUTH_SOURCE_KEY, 'password');
      this.refreshToken = refreshToken;
    } catch {
      this.forget();
      throw new PasswordAuthError('auth_unavailable', 503);
    }
  }

  private invalidateSession(): void {
    this.sessionGeneration += 1;
    this.refreshToken = null;
    this.currentSession = null;
    this.expiresAtMs = 0;
    this.refreshInFlight = null;
  }

  private forget(): void {
    this.invalidateSession();
    try {
      window.localStorage.removeItem(PASSWORD_REFRESH_TOKEN_KEY);
      window.localStorage.removeItem(PASSWORD_AUTH_SOURCE_KEY);
    } catch {
      // The in-memory credential is still cleared when persistent storage is unavailable.
    }
  }
}
